/**
 * v5.9 — Client-side device-bound persistent auth.
 *
 * При первом успешном login клиент:
 * 1. Генерит 32-byte random secret через Web Crypto API.
 * 2. Вычисляет fingerprint (UA + screen + timezone + canvas + WebGL).
 * 3. POST /api/auth/device/enroll → backend хранит bcrypt(secret).
 * 4. Сохраняет { id, secret, fingerprint } в IndexedDB.
 *
 * При следующих стартах если normal refresh fails (cookie cleared / expired):
 * 1. Читает creds из IDB.
 * 2. POST /api/auth/device/login → backend выдаёт fresh tokens.
 *
 * Безопасность:
 * - IndexedDB НЕ sync'ится Chrome Sync'ом между устройствами (в отличие
 *   от cookies). Каждое устройство имеет свой уникальный credential.
 * - Plaintext secret в IDB локально (не покидает устройство кроме enroll
 *   и subsequent device-logins).
 * - При logout — wipe IDB.
 */

const IDB_NAME = 'swipa-device-auth';
const IDB_STORE = 'cred';
const IDB_KEY = 'current';

interface DeviceCredential {
  id: string;
  fingerprint: string;   // sha256 hex
  /** v2 (предпочтительно): НЕИЗВЛЕКАЕМЫЙ приватный ключ ECDSA P-256, хранится в
   *  IndexedDB как CryptoKey (structured clone). XSS не может его выгрузить —
   *  только подписать challenge, пока вкладка открыта. */
  privateKey?: CryptoKey;
  /** v1 (legacy): plaintext 32-byte secret (base64). Мигрирует в v2 при апгрейде. */
  secret?: string;
  /** Версия: 2 = keypair, иначе legacy secret. */
  v?: number;
}

type ApiPoster = { post: (url: string, body?: unknown) => Promise<{ data: any }> };

/** ArrayBuffer → base64 (для SPKI публичного ключа и подписи). */
function bufToB64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

function openIDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(): Promise<DeviceCredential | null> {
  try {
    const db = await openIDB();
    return new Promise((resolve) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const st = tx.objectStore(IDB_STORE);
      const req = st.get(IDB_KEY);
      req.onsuccess = () => resolve((req.result as DeviceCredential) ?? null);
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

async function idbSet(cred: DeviceCredential): Promise<void> {
  try {
    const db = await openIDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).put(cred, IDB_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // Silent fail — IDB недоступен (Safari private mode, etc.) → клиент
    // вернётся к classic refresh-cookie flow без device-fallback.
  }
}

/**
 * id текущего device-credential этого браузера (из IndexedDB) или null.
 * Используется при logout-everywhere, чтобы сервер НЕ ревокал именно это
 * устройство (остальные — выкидывает).
 */
export async function getDeviceCredentialId(): Promise<string | null> {
  const cred = await idbGet();
  return cred?.id ?? null;
}

export async function clearDeviceCredential(): Promise<void> {
  try {
    const db = await openIDB();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).delete(IDB_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch {
    /* noop */
  }
}

/** SHA-256 hex (Web Crypto). Used both for fingerprint and challenge digests. */
async function sha256Hex(input: string): Promise<string> {
  const buf = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Compute a stable-ish fingerprint of this browser. */
export async function computeFingerprint(): Promise<string> {
  const parts: string[] = [];
  parts.push(navigator.userAgent || '');
  parts.push(`${screen.width}x${screen.height}x${screen.colorDepth}`);
  try {
    parts.push(Intl.DateTimeFormat().resolvedOptions().timeZone || '');
  } catch { /* ignore */ }
  parts.push((navigator.languages || []).slice(0, 4).join(','));
  parts.push(String(navigator.hardwareConcurrency || 0));
  parts.push(String((navigator as unknown as { deviceMemory?: number }).deviceMemory || 0));
  // Canvas fingerprint — small toDataURL hash.
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 200; canvas.height = 50;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('swipa-fingerprint-2026', 4, 4);
      parts.push(canvas.toDataURL().slice(-50));
    }
  } catch { /* ignore */ }
  // WebGL renderer.
  try {
    const gl = document.createElement('canvas').getContext('webgl');
    if (gl) {
      const dbg = gl.getExtension('WEBGL_debug_renderer_info');
      if (dbg) {
        parts.push(String(gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) || ''));
        parts.push(String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) || ''));
      }
    }
  } catch { /* ignore */ }
  return sha256Hex(parts.join('|'));
}

type DeviceUser = { id: string; username: string; displayName: string; avatarUrl: string | null; globalRole: string; emailVerified: boolean };
type DeviceLoginResult = { accessToken: string; user: DeviceUser };

/**
 * Enroll this device for passwordless persistent auth (v2, security #1):
 * генерим ECDSA P-256 пару с НЕИЗВЛЕКАЕМЫМ приватным ключом, на сервер шлём
 * только публичный (SPKI base64), приватный кладём в IndexedDB как CryptoKey.
 * XSS не может выгрузить приватный ключ — лишь подписать challenge, пока вкладка
 * открыта (нет переносимого «вечного» credential'а). Fire-and-forget.
 * Returns new credential id (или null при ошибке — caller игнорирует).
 */
export async function enrollDevice(apiClient: ApiPoster): Promise<string | null> {
  try {
    const fingerprint = await computeFingerprint();
    const keyPair = await crypto.subtle.generateKey(
      { name: 'ECDSA', namedCurve: 'P-256' },
      false, // privateKey non-extractable; publicKey всё равно экспортируется
      ['sign', 'verify'],
    );
    const spki = await crypto.subtle.exportKey('spki', keyPair.publicKey);
    const publicKey = bufToB64(spki);
    const res = await apiClient.post('/api/auth/device/enroll', { publicKey, fingerprint });
    const id = res.data.id as string;
    await idbSet({ id, fingerprint, privateKey: keyPair.privateKey, v: 2 });
    return id;
  } catch {
    return null;
  }
}

/**
 * Try to log in via stored device credentials. Dual-path:
 *  - v2 (privateKey): challenge-response — берём nonce, подписываем неизвлекаемым ключом.
 *  - v1 (secret): legacy — шлём secret напрямую.
 * Returns accessToken on success (refresh cookie ставит backend), null on failure.
 */
export async function tryDeviceLogin(apiClient: ApiPoster): Promise<DeviceLoginResult | null> {
  const cred = await idbGet();
  if (!cred) return null;
  try {
    // Re-compute fingerprint at login time (may drift if browser updated).
    const fingerprint = await computeFingerprint();
    if (cred.v === 2 && cred.privateKey) {
      const ch = await apiClient.post('/api/auth/device/challenge', { id: cred.id });
      const challenge: string = ch.data.challenge;
      const sigBuf = await crypto.subtle.sign(
        { name: 'ECDSA', hash: { name: 'SHA-256' } },
        cred.privateKey,
        new TextEncoder().encode(challenge),
      );
      const res = await apiClient.post('/api/auth/device/login', {
        id: cred.id,
        signature: bufToB64(sigBuf),
        challenge,
        fingerprint,
      });
      return { accessToken: res.data.accessToken, user: res.data.user };
    }
    // v1 legacy secret path.
    const res = await apiClient.post('/api/auth/device/login', {
      id: cred.id,
      secret: cred.secret,
      fingerprint,
    });
    return { accessToken: res.data.accessToken, user: res.data.user };
  } catch {
    // 401 = revoked / mismatch. Clean up local copy.
    await clearDeviceCredential();
    return null;
  }
}

/**
 * Миграция без разлогина: если в IndexedDB lежит legacy v1-secret (или ничего),
 * перевыпускаем устройство как v2 (keypair). Вызывать после успешной авторизации
 * (refresh / device-login) — апгрейдит существующих юзеров на следующем заходе,
 * после чего plaintext-secret из IDB исчезает. Fire-and-forget.
 */
export async function ensureDeviceV2(apiClient: ApiPoster): Promise<void> {
  try {
    const cred = await idbGet();
    if (cred && cred.v === 2 && cred.privateKey) return; // уже v2 — no-op
    await enrollDevice(apiClient); // перезапишет IDB новым v2-кредом
  } catch {
    /* noop */
  }
}
